import React, { useState } from 'react';
import styled from 'styled-components';
import { Alert, Button, InviteCard, InviteList, LoaderModal, Modal, ModalContent, ModalFooter, ModalText, ModalTitle } from 'components';
import { theme, toRem } from 'design';
import { IInvite, InvitationDisplay } from 'types';
import { getInvitedSitesList } from 'utils';

interface UserInvitesProps {
  isActive: boolean;
  handleIgnoreInvite: () => void;
  handleAcceptInvite: () => void;
  onClose: () => void;
  userInvites: Array<IInvite>;
  isLoading?: boolean;
}

const UserInviteModal = styled(Modal)`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    margin-bottom: ${toRem(theme.space.md)};
  }

  ul {
    margin-top: ${toRem(theme.space['3xl'])};
  }
`;

const UserInvites = ({ isActive, handleAcceptInvite, handleIgnoreInvite, isLoading, onClose, userInvites }: UserInvitesProps) => {
  const invitedSitesList: InvitationDisplay[] = getInvitedSitesList(userInvites);
  const isMultiInvite: boolean = userInvites.length > 1;
  const invitationText = isMultiInvite ? 'Invitations' : 'Invitation';
  const [ignoreAlertVisible, setIgnoreAlertVisible] = useState<boolean>(false);

  if (isLoading) return <LoaderModal isActive title={`Accepting ${invitationText}`} />;

  if (ignoreAlertVisible) {
    return (
      <Alert
        dataCy="ignore-invitation-alert"
        isActive={ignoreAlertVisible}
        title={`Ignore ${invitationText}`}
        message={isMultiInvite ? 'Are you sure you want to ignore these invitations? You may not be able to claim them later.' : 'Are you sure you want to ignore this invitation? You may not be able to claim it later.'}
        leftActionLabel="Ignore"
        onLeftAction={handleIgnoreInvite}
        rightActionLabel="Go back"
        onRightAction={() => setIgnoreAlertVisible(false)}
      />
    );
  }

  return (
    <UserInviteModal maxWidth={540} isActive={isActive} closeOnOutSideClick={false} hideCloseButton onClose={onClose} data-cy="user-invites-modal">
      <ModalTitle data-cy="modal-title">{`Accept ${invitationText}`}</ModalTitle>
      <ModalText data-cy="modal-text">Please review and accept to start receiving direct payments from your employers.</ModalText>
      <ModalContent>
        <InviteList data-cy="invite-list">
          {invitedSitesList.map((invite, i: number) => (
            <InviteCard data-cy="employee-invite-card" key={i} role={invite.displayRole} entity={invite.siteName || invite.orgName} />
          ))}
        </InviteList>
      </ModalContent>
      <ModalFooter columns={2}>
        <Button data-cy="ignore-invite-btn" label="Ignore" appearance="secondary" onClick={() => setIgnoreAlertVisible(true)} />
        <Button data-cy="accept-invite-btn" label={`Accept ${invitationText}`} onClick={handleAcceptInvite} />
      </ModalFooter>
    </UserInviteModal>
  );
};

export default UserInvites;
